// src/screens/ResetPasswordScreen.js
import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';

const ResetPasswordScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      navigate('/login?reset=success');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired reset link.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6">
        <p className="text-app-text-light dark:text-app-text mb-4">Invalid reset link.</p>
        <Link to="/login" className="text-customer-accent font-semibold hover:text-customer-accent-hover">
          Back to Login
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen lg:min-h-0 lg:py-12 lg:px-4 bg-gradient-to-br from-customer-teal-light/30 via-white to-customer-accent-light/30 dark:from-app-bg dark:via-app-bg dark:to-app-bg customer-friendly">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center py-8 lg:py-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-app-text-light dark:text-app-text mb-2">
            Set New Password
          </h1>
          <p className="text-app-text-secondary-light dark:text-app-text-secondary">
            Choose a new password for your account.
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex-1 p-6 lg:p-8 space-y-5 text-sm w-full max-w-md mx-auto bg-white dark:bg-app-bg-secondary rounded-3xl shadow-xl shadow-customer-teal/10 border border-app-border-light/50 dark:border-app-border"
      >
        <div>
          <label className="text-sm font-medium text-app-text-light dark:text-app-text-secondary">
            New Password
          </label>
          <input
            type="password"
            placeholder="At least 6 characters"
            className="mt-2 w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-teal/50 focus:border-customer-teal transition-all"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <div>
          <label className="text-sm font-medium text-app-text-light dark:text-app-text-secondary">
            Confirm Password
          </label>
          <input
            type="password"
            placeholder="Repeat your new password"
            className="mt-2 w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-teal/50 focus:border-customer-teal transition-all"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </div>

        {error && (
          <p className="text-red-500 text-sm">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-4 w-full py-4 rounded-2xl bg-gradient-to-r from-customer-teal to-customer-teal-hover hover:from-customer-teal-hover hover:to-customer-teal-active text-white text-base font-bold shadow-lg shadow-customer-teal/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : 'Reset Password'}
        </button>

        <div className="text-sm text-center text-app-text-secondary-light dark:text-app-text-secondary pt-4 border-t border-app-border-light/50 dark:border-app-border">
          <Link to="/login" className="text-customer-accent font-semibold hover:text-customer-accent-hover transition-colors">
            Back to Login
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ResetPasswordScreen;
